import {
  Button,
  IconImages,
  IconTrashCan,
  IndigoIndigo60,
  Spinner,
  useNotifications,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  COVER_IMAGE_ACCEPTED_TYPES,
  useSelectSignedPdfCoverImageSrc,
  useSelectPdfCoverImageSrc,
  useStorePdfCoverImageAsset
} from 'Cube/domain/subdomain/coverImage'
import { UploadAssetButton } from 'Assets/index'
import { useUploadAssets } from 'Assets/hooks/useUploadAssets'
import type { ServerAsset } from '@sequencehq/api/utils/commonModels'
import { constructAssetPath } from '@sequencehq/utils'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { FC, useEffect, useMemo, useRef, useState } from 'react'

type PdfCoverImagePreviewProps = {
  isDisabled: boolean
  shouldTriggerFilePicker?: boolean
  onFilePickerTriggered?: () => void
  onFilePickerCancelled?: () => void
  onRemove?: () => void
}

export const PdfCoverImagePreview: FC<PdfCoverImagePreviewProps> = ({
  isDisabled,
  shouldTriggerFilePicker,
  onFilePickerTriggered,
  onFilePickerCancelled,
  onRemove
}) => {
  const pdfCoverImageSrc = useSelectPdfCoverImageSrc()
  const signedPdfCoverImageSrc = useSelectSignedPdfCoverImageSrc()
  const storePdfCoverImageAsset = useStorePdfCoverImageAsset()
  const uploadAssets = useUploadAssets()
  const { displayNotification } = useNotifications()
  const { data: sequenceAccountSettings } = useQuery(
    dashboard20240730Client.getSequenceAccountSettings
  )

  const [isUploading, setIsUploading] = useState(false)
  const [previewSrc, setPreviewSrc] = useState<string>()

  const hasPdfCoverImage = Boolean(pdfCoverImageSrc)

  const handleUploadSuccess = (serverAsset: ServerAsset) => {
    if (!sequenceAccountSettings) {
      handleUploadFailure('Unable to get account ID')
      return
    }

    setIsUploading(false)
    storePdfCoverImageAsset(
      constructAssetPath({
        accountId: sequenceAccountSettings.sequenceAccountId,
        assetId: serverAsset.id
      })
    )
  }

  const handleUploadFailure = (error: string) => {
    setIsUploading(false)
    setPreviewSrc(undefined)
    displayNotification(`Upload failed: ${error}`, {
      type: 'error'
    })
  }

  const handleFilesPicked = (pickedFiles: File[]) => {
    const pickedFile = pickedFiles.at(0)
    if (!pickedFile) return

    setIsUploading(true)
    setPreviewSrc(URL.createObjectURL(pickedFile))
    uploadAssets({
      files: [pickedFile],
      onUploadSuccess: handleUploadSuccess,
      onUploadFailure: handleUploadFailure
    })
  }

  const handleRemove = () => {
    setPreviewSrc(undefined)
    storePdfCoverImageAsset(null)
    onRemove?.()
  }

  useEffect(() => {
    if (!previewSrc?.startsWith('blob:')) return
    return () => URL.revokeObjectURL(previewSrc)
  }, [previewSrc])

  const uploadButtonRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const fileInput =
      uploadButtonRef.current?.querySelector('input[type="file"]')
    const handleCancel = () => onFilePickerCancelled?.()
    fileInput?.addEventListener('cancel', handleCancel)
    return () => {
      fileInput?.removeEventListener('cancel', handleCancel)
    }
  }, [onFilePickerCancelled])

  useEffect(() => {
    if (!shouldTriggerFilePicker) return
    const button = uploadButtonRef.current?.querySelector('button')
    button?.click()
    onFilePickerTriggered?.()
  }, [shouldTriggerFilePicker, onFilePickerTriggered])

  const showImage = hasPdfCoverImage || isUploading
  const showControls = hasPdfCoverImage && !isUploading
  const imageSrc = previewSrc ?? signedPdfCoverImageSrc

  const backgroundImage = useMemo(() => {
    if (imageSrc?.startsWith('blob:')) {
      return `url(${imageSrc})`
    }

    if (signedPdfCoverImageSrc) {
      return `url(${signedPdfCoverImageSrc})`
    }

    return undefined
  }, [imageSrc, signedPdfCoverImageSrc])

  return (
    <>
      <Box
        ref={uploadButtonRef}
        position="absolute"
        overflow="hidden"
        width={0}
        height={0}
      >
        <UploadAssetButton
          onFilesPicked={handleFilesPicked}
          onUploadSuccess={handleUploadSuccess}
          onUploadFailure={handleUploadFailure}
          accept={COVER_IMAGE_ACCEPTED_TYPES}
          data-testid="quote-inspector-settings-upload-pdf-cover-image"
        >
          Add cover
        </UploadAssetButton>
      </Box>

      {showImage && (
        <Box position="relative" role="group">
          <Flex
            position="relative"
            justifyContent="center"
            alignItems="center"
            height="138px"
            borderRadius="4px"
            overflow="hidden"
            backgroundImage={backgroundImage}
            backgroundSize="100% auto"
            backgroundPosition="50% 50%"
            backgroundRepeat="no-repeat"
          >
            {isUploading && (
              <Spinner color={IndigoIndigo60} width="36px" height="36px" />
            )}

            {showControls && (
              <Box
                _groupHover={{ opacity: 1 }}
                opacity={0}
                transition="opacity 0.15s ease-out"
              >
                <Flex gap="4px" position="absolute" top="8px" right="8px">
                  <UploadAssetButton
                    leadingIcon={<IconImages />}
                    variant="secondary"
                    onFilesPicked={handleFilesPicked}
                    onUploadSuccess={handleUploadSuccess}
                    onUploadFailure={handleUploadFailure}
                    accept={COVER_IMAGE_ACCEPTED_TYPES}
                    disabled={isDisabled}
                    data-testid="quote-inspector-settings-replace-pdf-cover-image"
                  >
                    Replace
                  </UploadAssetButton>
                  <Button
                    variant="secondary"
                    icon={<IconTrashCan />}
                    onClick={handleRemove}
                    disabled={isDisabled}
                    data-testid="quote-inspector-settings-remove-pdf-cover-image"
                    aria-label="Remove"
                  />
                </Flex>
              </Box>
            )}
          </Flex>
        </Box>
      )}
    </>
  )
}
